import React, { useState } from "react";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { useReportQuery } from "../shared/useReportQuery";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { centsToUsd, formatMetric, METRIC_LABELS } from "../shared/format";
import { ToggleButton } from "../../../components/Button";
import { MetricKey, ReportResponse, ReportType } from "../api/types";

const SUMMARY_METRICS: MetricKey[] = ["Units", "ItemRevenue", "ItemTax", "ItemDiscount"];

const TOOLTIP =
  "Product mix from the NEW unified reporting engine (item grain). " +
  "Item Revenue = Σ line subtotal (pre-tax, pre-discount). Gross = Item Revenue + Item Tax. " +
  "Net is the canonical real-money total that matches the Financial reports. " +
  "Switch between per-item and per-category aggregation.";

const NET_LEGEND =
  "Net = real money after discounts & refunds, capped at collected (matches Financial reports)";

type MixMode = "item" | "category";

const MODE_CONFIG: Record<
  MixMode,
  { type: ReportType; keyHeader: string; groupHeader?: string }
> = {
  item: { type: "ProductMixByItem", keyHeader: "Item", groupHeader: "Category" },
  category: { type: "ProductMixByCategory", keyHeader: "Category" },
};

// Adds the per-line Gross column. The engine returns per-line gross under the PascalCase metric key
// `values.Gross` (and `totals.Gross`). We render it as an internal `ItemGross` metric placed right
// after `ItemTax` so it carries the correct item-grain label + formula ("Item Revenue + Item Tax")
// in the column header, tooltip, and CSV legend — distinct from the order-grain "Gross Sales". We also
// strip any raw `Gross` from `metrics` so it can't render a second, mislabeled column.
function withGrossColumn(d: ReportResponse): ReportResponse {
  const grossOf = (values: Partial<Record<MetricKey, number>>): Partial<Record<MetricKey, number>> => ({
    ...values,
    ItemGross: values.Gross ?? (values.ItemRevenue ?? 0) + (values.ItemTax ?? 0),
  });
  const metrics = d.metrics.filter((m) => m !== "Gross");
  if (!metrics.includes("ItemGross")) {
    const i = metrics.indexOf("ItemTax");
    metrics.splice(i >= 0 ? i + 1 : metrics.length, 0, "ItemGross");
  }
  return {
    ...d,
    metrics,
    rows: d.rows.map((r) => ({ ...r, values: grossOf(r.values) })),
    totals: grossOf(d.totals),
  };
}

// Parallel to the OLD Product Mix Report. Item-grain rollup: Units, Item Revenue, Item Tax, Gross
// (= revenue + tax), Item Discount. Summary adds Gross + Net headline cards (Net = totals.NetSales,
// matches the Financial reports). No per-line Net column — that'd be an allocation/estimate.
export default function ProductMixReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [mode, setMode] = useState<MixMode>("item");
  const cfg = MODE_CONFIG[mode];
  // Cap rows generously; totals always reflect the full unscoped set.
  const state = useReportQuery(toQueryParams(cfg.type, filters, { limit: 1000 }));

  return (
    <ReportPageShell
      title="Product Mix"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      actions={
        <>
          <ToggleButton isSelected={mode === "item"} onClick={() => setMode("item")}>
            By Item
          </ToggleButton>
          <ToggleButton
            isSelected={mode === "category"}
            onClick={() => setMode("category")}
          >
            By Category
          </ToggleButton>
        </>
      }
    >
      {(data) => {
        const aug = withGrossColumn(data);
        const grossTotal =
          data.totals.Gross ?? (data.totals.ItemRevenue ?? 0) + (data.totals.ItemTax ?? 0);
        const netTotal = data.totals.NetSales; // canonical net — totals only, never per-line
        const csvExtra = [
          "",
          NET_LEGEND,
          `Net (total): ${netTotal != null ? centsToUsd(netTotal) : "pending engine update"}`,
        ];

        return (
          <>
            <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {SUMMARY_METRICS.filter((m) => data.metrics.includes(m)).map((m) => (
                <StatCard key={m} label={METRIC_LABELS[m]} value={formatMetric(m, data.totals[m])} />
              ))}
              <StatCard label="Gross" value={centsToUsd(grossTotal)} />
              {/* Net is the headline number that matches the Financial reports. */}
              <StatCard label="Net" value={netTotal != null ? centsToUsd(netTotal) : "—"} emphasis />
            </div>
            <MetricGrid
              data={aug}
              keyColumnHeader={cfg.keyHeader}
              groupColumnHeader={mode === "item" ? cfg.groupHeader : undefined}
              exportName={`ProductMix_${mode}`}
              csvExtra={csvExtra}
            />
          </>
        );
      }}
    </ReportPageShell>
  );
}
